import { Children, forwardRef } from 'react';
import { Skeleton as MuiSkeleton, SkeletonProps as MuiSkeletonProps } from '@mui/material';

/**
 * Skeleton component for loading placeholders.
 *
 * A thin wrapper around Material-UI's Skeleton that provides loading state
 * placeholders with theme integration and enhanced accessibility.
 *
 * @example
 * // Text placeholder
 * <Skeleton variant="text" width="100%" />
 *
 * @example
 * // Avatar placeholder
 * <Skeleton variant="circular" width={40} height={40} />
 *
 * @example
 * // Card placeholder with no animation
 * <Skeleton variant="rectangular" width={210} height={118} animation={false} />
 *
 * @example
 * // Responsive width/height using sx prop
 * <Skeleton variant="rectangular" sx={{ width: { xs: '100%', md: 400 }, height: { xs: 100, md: 150 } }} />
 *
 * @see https://mui.com/material-ui/react-skeleton/
 */
export interface SkeletonProps extends MuiSkeletonProps {
  /**
   * The variant to use.
   * @default 'text'
   */
  variant?: 'text' | 'rectangular' | 'circular' | 'rounded';

  /**
   * The animation type. Use `false` to disable animation.
   * @default 'pulse'
   */
  animation?: 'pulse' | 'wave' | false;

  /**
   * Accessible label for the skeleton loading state.
   * @default 'Loading...'
   */
  'aria-label'?: string;
}

/**
 * Skeleton component for loading placeholders.
 *
 * Displays a placeholder preview of content before data loads to improve
 * perceived performance. Supports multiple variants (text, rectangular,
 * circular, rounded) and animations (pulse, wave, or none).
 */
export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(
  (
    {
      variant = 'text',
      animation = 'pulse',
      'aria-label': ariaLabel = 'Loading...',
      children,
      ...props
    },
    ref
  ) => (
    <MuiSkeleton
      ref={ref}
      variant={variant}
      animation={animation}
      aria-label={ariaLabel}
      aria-busy={Children.count(children) === 0}
      {...props}
    >
      {children}
    </MuiSkeleton>
  )
);

Skeleton.displayName = 'Skeleton';
